import { MapLabel } from './label';
import { MapLegend } from './legend';
import { MapLine } from './line';
import { MapLocation } from './location';
import { MapRecord, MapRecords } from './record';
import { MapRegion } from './region';
import { MainSettings } from './settings';
import { MapState } from './state';

/**
 * The map data we inject into the HTML5USMaps library from a source we import
 */
export class MapData {
  /**
   * The map settings
   */
  main_settings?: MainSettings;

  /**
   * Labels for the map
   */
  labels?: MapRecords<MapLabel>;

  /**
   * Map data marked as red dots
   */
  locations?: MapRecords<MapLocation>;

  /**
   * Lines for the map
   */
  lines?: MapRecords<MapLine>;

  /**
   * Regions within the map
   */
  regions?: MapRecords<MapRegion>;

  /**
   * State specific data
   */
  state_specific?: MapRecords<MapState>;

  /**
   * The map legend
   */
  legend?: MapLegend;

  constructor(data: Partial<MapData>) {
    Object.assign(this, data);
  }

  /**
   * Get a MapLocation using its key
   * @param {string} needle The key of the MapLocation
   * @returns {MapLocation | undefined}
   */
  public getLocation(needle: string): MapLocation | undefined {
    return new MapLocation((this.locations ?? {})[needle] || {});
  }

  /**
   * Get a MapRegion using its key
   * @param {string} needle The key of the MapRegion
   * @returns {MapRegion | undefined}
   */
  public getRegion(needle: string): MapRegion | undefined {
    return (this.regions ?? {})[needle];
  }

  /**
   * Get a MapState using its key
   * @param {string} needle The key of the MapState
   * @returns {MapState | undefined}
   */
  public getState(needle: string): MapState | undefined {
    const keys = Object.keys(this.state_specific ?? {});
    if (keys.includes(needle)) return new MapState((this.state_specific ?? {})[needle]);
    else {
      for (const key of keys) {
        if (this.state_specific?.[key]?.COC_StateCode === needle) return new MapState((this.state_specific ?? {})[key]);
      }
    }

    return undefined;
  }

  /**
   * Get a MapLabel using its key
   * @param {string} needle The key of the MapLabel
   * @returns {MapLabel | undefined}
   */
  public getLabel(needle: string): MapLabel | undefined {
    return (this.labels ?? {})[needle];
  }

  /**
   * Get a MapLine using its key
   * @param {string} needle The key of the MapLine
   * @returns {MapLine | undefined}
   */
  public getLine(needle: string): MapLine | undefined {
    return (this.lines ?? {})[needle];
  }

  /**
   * Get or create a MapRecord using a unique key
   * @param {string | number} key The key of the MapRecord
   * @param {MapRecords<T>} records The records to search through
   * @param {MapRecord} factory The factory to create a new instance of the MapRecord
   * @returns {T}
   */
  public getOrCreateObjectFromKey<T extends MapRecord>(key: string | number, records: MapRecords<T>, factory: new (data: T) => T): T {
    // Find the needle in the haystack
    const needle = records[key];

    // If we have a needle, return a new instance of the factory
    if (needle) return new factory(needle);

    // Otherwise, return a new instance of the factory with an empty object
    return new factory(records[key] || {});
  }
}
